// VersionStatusCard — prominent CalVer + update-channel block on
// AdminOverview (item 69 of PRODUCT-ROADMAP.md, delivery #77).
//
// Three things at a glance:
//   1. Installed version, big and prominent.
//   2. Build timestamp + "released N days ago" so an auditor can
//      see at a glance whether they're running fresh or stale code.
//   3. CompareKind badge ("up to date" / "patch behind" /
//      "year behind" / "ahead" / "update check disabled").
//
// Air-gapped + opted-out installs render the badge as
// "update check disabled" with the reason inline, so an operator
// looking at the page doesn't think the system is unsupported when
// the gate is just blocking the egress check.

import { useState } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
  Info,
  RefreshCw,
  CircleHelp,
} from 'lucide-react';
import {
  useVersionStatus,
  checkForUpdatesNow,
  type CompareKind,
  type VersionStatus,
} from '../api/versionStatus';
import { Card, CardBody } from './ui/Card';
import { Button } from './ui/Button';
import { useSWRConfig } from 'swr';

interface BadgeShape {
  Icon: typeof CheckCircle2;
  label: string;
  // Tone background + border classes — matches the AdminOverview
  // existing tone vocabulary (success / warning / danger / accent).
  tone: string;
}

function compareBadge(kind: CompareKind, latest: string | undefined): BadgeShape | null {
  switch (kind) {
    case 'up_to_date':
      return {
        Icon: CheckCircle2,
        label: 'Up to date',
        tone: 'border-success/40 bg-success/5 text-success',
      };
    case 'patch_behind':
      return {
        Icon: AlertTriangle,
        label: latest ? `Patch available — ${latest}` : 'Patch available',
        tone: 'border-warning/40 bg-warning/10 text-warning',
      };
    case 'major_behind':
      return {
        Icon: Info,
        label: latest ? `New release available — ${latest}` : 'New release available',
        tone: 'border-accent/40 bg-accent/10 text-accent',
      };
    case 'year_behind':
      return {
        Icon: AlertOctagon,
        label: latest ? `Year behind — ${latest}` : 'Year behind',
        tone: 'border-danger/40 bg-danger/10 text-danger',
      };
    case 'ahead':
      return {
        Icon: Info,
        label: latest ? `Ahead of channel — latest is ${latest}` : 'Ahead of channel',
        tone: 'border-accent/40 bg-accent/10 text-accent',
      };
    case 'unknown':
    default:
      return null;
  }
}

function fmtAge(iso: string | undefined): string {
  if (!iso) return '';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const days = Math.floor((Date.now() - ts) / 86_400_000);
  if (days < 0) return ''; // future build clock skew
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 month ago';
  if (months < 24) return `${months} months ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

function disabledNote(s: VersionStatus): string {
  if (!s.disabled_reason) return 'Update check disabled';
  if (s.disabled_reason.startsWith('air-gapped')) {
    return 'Update check disabled — air-gapped';
  }
  return 'Update check disabled';
}

export function VersionStatusCard() {
  const { data, mutate } = useVersionStatus();
  const { mutate: globalMutate } = useSWRConfig();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!data) {
    return null;
  }

  const badge = data.enabled ? compareBadge(data.compare, data.latest_version) : null;
  const buildAge = fmtAge(data.build_time);

  async function onCheckNow() {
    setError(null);
    setChecking(true);
    try {
      const fresh = await checkForUpdatesNow();
      await mutate(fresh, { revalidate: false });
      // Re-fetch the public-config endpoint too — SeedBanner /
      // air-gapped status share the same envelope and may have
      // shifted while the user was on this page.
      void globalMutate('/api/public-config');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setChecking(false);
    }
  }

  return (
    <Card>
      <CardBody className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="orb-eyebrow text-fg-muted">OrbitalReg version</p>
            <p className="text-2xl font-semibold font-mono text-fg mt-1 break-all">
              {data.installed || 'dev'}
              {!data.installed_is_calver && (
                <span
                  className="ml-2 inline-flex items-center gap-1 align-middle text-xs font-sans font-normal text-fg-muted"
                  title="Pre-CalVer build (working-tree or pre-release tag). The first production CalVer release is 2026.1.0."
                >
                  <CircleHelp className="h-3.5 w-3.5" />
                  pre-CalVer
                </span>
              )}
            </p>
            <p className="text-sm text-fg-muted mt-1">
              {data.build_time
                ? (
                  <>
                    Built {new Date(data.build_time).toLocaleString()}
                    {buildAge && <> · <span>{buildAge}</span></>}
                  </>
                )
                : 'Build time unknown'}
              {data.commit && (
                <>
                  {' '}· <span className="font-mono">commit {data.commit.slice(0, 12)}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {badge && (
              <span
                className={
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-sm font-medium ' +
                  badge.tone
                }
              >
                <badge.Icon className="h-4 w-4" />
                {badge.label}
              </span>
            )}
            {!data.enabled && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-bg-inset text-sm text-fg-muted"
                title={data.disabled_reason}
              >
                <CircleHelp className="h-4 w-4" />
                {disabledNote(data)}
              </span>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={onCheckNow}
              disabled={checking || !data.enabled}
              title={data.enabled ? 'Pull manifest now' : data.disabled_reason ?? 'disabled'}
            >
              <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
              {checking ? 'Checking…' : 'Check now'}
            </Button>
          </div>
        </div>

        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}

        {data.enabled && data.last_error && !error && (
          <p className="text-sm text-warning">
            Last update check failed: {data.last_error}
          </p>
        )}

        {data.enabled && data.latest_version && (
          <p className="text-sm text-fg-muted">
            Latest available: <span className="font-mono text-fg">{data.latest_version}</span>
            {data.latest_released_at && (
              <> · released {new Date(data.latest_released_at).toLocaleDateString()}</>
            )}
            {data.latest_channel && <> · channel <span className="font-mono">{data.latest_channel}</span></>}
            {data.latest_checked_at && (
              <> · checked {fmtAge(data.latest_checked_at)}</>
            )}
          </p>
        )}

        {!data.enabled && data.disabled_reason && (
          <p className="text-sm text-fg-muted">
            {data.disabled_reason}. Configure the channel under <span className="font-mono">/admin/network</span> (air-gapped Telemetry toggle) or set a manifest URL via <span className="font-mono">PUT /api/admin/version-status/settings</span>.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
